<template>
  <section
    class="mt-4 rounded-xl border border-boba-grey/50 bg-boba-sand-light shadow-sm px-5 py-5 xl:px-8 xl:py-6"
  >
    <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 class="text-xl xl:text-2xl font-semibold tracking-tight text-boba-brown">
        {{ title }}
      </h2>
      <p
        v-if="bids.length"
        data-test="bids-summary"
        class="text-sm text-boba-grey-dark tabular-nums"
      >
        {{ bids.length }} {{ bids.length === 1 ? "bid" : "bids" }} · up to
        {{ $format(fromBaseUnits(combinedMax)) }} miles
      </p>
    </div>

    <div
      v-if="!bids.length"
      class="mt-4 rounded-lg border border-dashed border-boba-grey bg-white/60 px-4 py-6 text-center"
    >
      <i class="fa-light fa-gavel text-2xl text-boba-grey-dark" aria-hidden="true" />
      <p class="mt-2 text-sm font-medium text-boba-brown">
        You have no active bids yet.
      </p>
      <p class="mt-1 text-xs text-boba-grey-dark">
        Bids you place will appear here. Cancel any of them until the auction
        closes.
      </p>
    </div>

    <div
      v-else
      class="mt-4 rounded-lg border border-boba-grey/50 bg-white overflow-hidden"
    >
      <div
        class="hidden xl:grid xl:grid-cols-[minmax(7rem,1.2fr)_5.5rem_minmax(6rem,1fr)_minmax(8rem,1fr)_auto] gap-x-4 items-center px-5 py-2 bg-boba-sand-light/70 border-b border-boba-grey/50 text-xs font-semibold uppercase tracking-wide text-boba-grey-dark"
        aria-hidden="true"
      >
        <span>Max / ea</span>
        <span>Qty</span>
        <span>Max total</span>
        <span>Placed</span>
        <span class="invisible justify-self-end inline-flex items-center gap-1.5 px-3 text-sm font-medium">
          <i class="fa-regular fa-trash-can text-xs" /> Cancel
        </span>
      </div>

      <ul class="divide-y divide-boba-grey/50">
        <li
          v-for="bid in bids"
          :key="`bid-${bid.id}`"
          data-test="bid-row"
          class="relative grid grid-cols-1 gap-y-1 items-center pl-4 pr-16 py-3.5 xl:grid-cols-[minmax(7rem,1.2fr)_5.5rem_minmax(6rem,1fr)_minmax(8rem,1fr)_auto] xl:gap-x-4 xl:px-5 xl:py-3.5 hover:bg-boba-sand-light/60 transition-colors"
        >
          <p class="text-2xl xl:text-lg font-semibold leading-tight tabular-nums text-boba-brown whitespace-nowrap xl:col-start-1 xl:row-start-1">
            {{ $format(bid.bid_amount / 1e9) }}
            <span class="text-sm font-normal text-boba-grey-dark"
              >miles<span class="xl:hidden"> / ea</span></span
            >
          </p>

          <div class="absolute top-2 right-2 xl:static xl:col-start-5 xl:row-start-1 xl:justify-self-end">
            <button
              v-if="canCancel"
              data-test="cancel"
              type="button"
              class="inline-flex items-center justify-center gap-1.5 h-11 w-11 rounded-full xl:h-auto xl:w-auto xl:rounded-lg xl:px-3 xl:py-1.5 border border-transparent xl:border-boba-grey/70 bg-transparent xl:bg-white text-boba-grey-dark xl:text-sm xl:font-medium hover:text-red-700 xl:hover:border-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700/60 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-boba-grey-dark xl:disabled:hover:border-boba-grey/70 transition-colors"
              :disabled="cancelling"
              :title="`Cancel bid`"
              :aria-label="`Cancel bid of ${$format(bid.bid_amount / 1e9)} miles for ${bid.bid_quantity}`"
              @click="emit('cancel', bid)"
            >
              <i class="fa-regular fa-trash-can text-base xl:text-xs" aria-hidden="true" />
              <span class="hidden xl:inline">Cancel</span>
            </button>
          </div>

          <div
            class="col-start-1 flex items-baseline gap-x-1.5 text-base text-boba-brown xl:contents"
          >
            <p class="tabular-nums xl:col-start-2 xl:row-start-1 xl:text-base">
              <span class="text-boba-grey-dark xl:hidden">Qty</span>
              {{ $format(bid.bid_quantity) }}
            </p>
            <span class="text-boba-grey-dark xl:hidden" aria-hidden="true">·</span>
            <p class="tabular-nums whitespace-nowrap xl:col-start-3 xl:row-start-1 xl:text-base">
              <span class="text-boba-grey-dark xl:hidden">up to</span>
              {{ $format(fromBaseUnits(mulBaseUnits(bid.bid_amount, bid.bid_quantity))) }}
              miles
            </p>
          </div>

          <p class="col-start-1 xl:col-start-4 xl:row-start-1 text-sm text-boba-grey-dark whitespace-nowrap">
            <span class="xl:hidden">Placed </span
            >{{ DateTime.fromISO(bid.created_at).toFormat("MMM d, h:mm a") }}
          </p>
        </li>
      </ul>
    </div>

    <p
      v-if="error"
      role="alert"
      class="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
    >
      <i class="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
      <span>{{ error.message }}</span>
    </p>

    <p v-if="bids.length" class="mt-3 text-xs text-boba-grey-dark">
      Nothing is charged until the auction closes. You can cancel any bid before
      then.
    </p>
  </section>
</template>
<script lang="ts" setup>
import { computed } from "vue";
import { DateTime } from "ts-luxon";
import { Errors, ListingBid } from "@/types";
import { fromBaseUnits, mulBaseUnits } from "@/util/miles";

const props = withDefaults(
  defineProps<{
    bids: ListingBid[];
    canCancel: boolean;
    cancelling?: boolean;
    error?: Errors.CancelBidError | null;
    title?: string;
  }>(),
  { cancelling: false, error: null, title: "Your active bids" },
);

const emit = defineEmits<{
  (e: "cancel", bid: ListingBid): void;
}>();

/** Sum of every bid's maximum total, in base units. */
const combinedMax = computed(() =>
  props.bids
    .reduce(
      (sum, bid) => sum + BigInt(mulBaseUnits(bid.bid_amount, bid.bid_quantity)),
      0n,
    )
    .toString(),
);
</script>
